import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCamera, FaSave, FaTimes } from 'react-icons/fa';
import { getUserProfile, updateUserProfile } from '../services/userService';
import { useAuth } from '../context/AuthContext';

const ProfileEditPage = () => {
    const { currentUser, updateUser } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
    });

    const [profileImage, setProfileImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setInitialLoading(true);
                const response = await getUserProfile();

                if (response.success) {
                    const user = response.user;
                    setFormData({
                        name: user.name || '',
                        email: user.email || '',
                        phone: user.phone || '',
                        address: user.address || '',
                    });

                    if (user.profileImage) {
                        setPreviewUrl(user.profileImage);
                    }
                }
            } catch (error) {
                setError('Profil bilgileri yüklenirken bir hata oluştu');
                console.error(error);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            setError('');
            setSuccess('');

            const data = new FormData();
            data.append('name', formData.name);
            data.append('email', formData.email);
            data.append('phone', formData.phone);
            data.append('address', formData.address);

            if (profileImage) {
                data.append('profileImage', profileImage);
            }

            const response = await updateUserProfile(data);

            if (response.success) {

                console.log('✅ ProfileEdit - UPDATE RESPONSE:', response);
                console.log('🖼️ ProfileEdit - Profile Image URL:', response.user?.profileImage);
                console.log('📦 ProfileEdit - Updated User:', response.user);

                setSuccess('Profil başarıyla güncellendi');

                if (typeof updateUser === 'function') {
                    updateUser(response.user);
                }

                // localStorage'ı da güncelle
                const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUserData = {
                    ...currentUserData,
                    ...response.user
                };
                localStorage.setItem('user', JSON.stringify(updatedUserData));

                setTimeout(() => {
                    navigate('/profile');
                }, 2000);
            } else {
                setError(response.message || 'Profil güncellenirken bir hata oluştu');
            }
        } catch (error) {
            setError('Sunucu hatası: ' + (error.response?.data?.message || error.message));
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getOptimizedProfileImage = (url, size = 200) => {
        if (!url) return '';

        if (url.includes('cloudinary.com')) {
            try {
                const parts = url.split('/upload/');
                return `${parts[0]}/upload/w_${size},h_${size},c_fill,g_face/${parts[1]}`;
            } catch (e) {
                return url;
            }
        }

        return url;
    };

    if (initialLoading) {
        return (
            <div className="profile-edit-page">
                <section className="py-5">
                    <Container>
                        <div className="text-center">
                            <Spinner animation="border" role="status" style={{ color: 'var(--black)' }}>
                                <span className="visually-hidden">Yükleniyor...</span>
                            </Spinner>
                            <p className="mt-3">Profil bilgileri yükleniyor...</p>
                        </div>
                    </Container>
                </section>
            </div>
        );
    }

    return (
        <div className="profile-edit-page">
            {/* Header Section */}
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h1 className="section-title">Profil Düzenle</h1>
                        <p className="lead">Kişisel bilgilerinizi güncelleyin</p>
                    </div>
                </Container>
            </section>

            {/* Edit Form Section */}
            <section className="py-5 bg-light">
                <Container>
                    <Row className="g-4">
                        {/* Profile Image Section */}
                        <Col lg={4}>
                            <div className="modern-faq-item text-center">
                                <h3 className="modern-faq-question mb-4">Profil Fotoğrafı</h3>

                                <div className="profile-image-container mb-4">
                                    {previewUrl ? (
                                        <img
                                            src={getOptimizedProfileImage(previewUrl)}
                                            alt="Profil Fotoğrafı"
                                            className="rounded-circle img-fluid"
                                            style={{
                                                width: '200px',
                                                height: '200px',
                                                objectFit: 'cover',
                                                border: '4px solid var(--white)',
                                                boxShadow: 'var(--shadow)',
                                                margin: '0 auto'
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="default-profile-image rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                            style={{
                                                width: '200px',
                                                height: '200px',
                                                backgroundColor: 'var(--gray-100)',
                                                border: '4px solid var(--white)',
                                                boxShadow: 'var(--shadow)'
                                            }}
                                        >
                                            <FaUser style={{ fontSize: '5rem', color: 'var(--gray-400)' }} />
                                        </div>
                                    )}
                                </div>

                                <Form.Group controlId="profileImage" className="mb-3">
                                    <Form.Label className="btn btn-outline-dark w-100">
                                        <FaCamera className="me-2" />
                                        Fotoğraf Seç
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                    <Form.Text className="text-muted d-block mt-2">
                                        2MB'dan küçük JPG, PNG veya JPEG dosyası yükleyin
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </Col>

                        {/* Form Section */}
                        <Col lg={8}>
                            <div className="modern-faq-item">
                                <h3 className="modern-faq-question mb-4">Kişisel Bilgiler</h3>

                                {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
                                {success && <Alert variant="success" className="mb-4">{success}</Alert>}

                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="name">
                                                <Form.Label>
                                                    <FaUser className="me-2" />
                                                    Ad Soyad
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Adınız Soyadınız"
                                                    required
                                                    className="py-3"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="email">
                                                <Form.Label>
                                                    <FaEnvelope className="me-2" />
                                                    E-posta Adresi
                                                </Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="ornek@mail.com"
                                                    required
                                                    className="py-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="phone">
                                                <Form.Label>
                                                    <FaPhone className="me-2" />
                                                    Telefon
                                                </Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="0(5XX) XXX XX XX"
                                                    className="py-3"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-4" controlId="address">
                                                <Form.Label>
                                                    <FaMapMarkerAlt className="me-2" />
                                                    Adres
                                                </Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    rows={3}
                                                    placeholder="Adres bilgileriniz"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex gap-3">
                                        <Button
                                            type="submit"
                                            className="apply-btn-black flex-fill py-3"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Güncelleniyor...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave className="me-2" />
                                                    Profili Güncelle
                                                </>
                                            )}
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="outline-dark"
                                            className="flex-fill py-3"
                                            onClick={() => navigate('/profile')}
                                        >
                                            <FaTimes className="me-2" />
                                            İptal
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ProfileEditPage;